<script setup>
import { useForm, router } from '@inertiajs/vue3';
import { ref, computed, getCurrentInstance } from 'vue';
import AdminMainLayout from '@/layout/admin/AdminMainLayout.vue';
import Card from 'primevue/card';
import Button from 'primevue/button';
import MultiSelect from 'primevue/multiselect';
import Textarea from 'primevue/textarea';
import SelectButton from 'primevue/selectbutton';
import Divider from 'primevue/divider';
import ToggleSwitch from 'primevue/toggleswitch';
import TabView from 'primevue/tabview';
import TabPanel from 'primevue/tabpanel';
import Tag from 'primevue/tag';
import { useToast } from 'primevue/usetoast';
import { useLoading } from '@/composables/useLoading';

const { proxy } = getCurrentInstance();
const $t = proxy.$t;
const toast = useToast();
const { showLoading, hideLoading } = useLoading();

defineOptions({
    layout: AdminMainLayout,
});

const props = defineProps({
    settings: Object,
    adminUsers: Array,
    categories: Array,
});

// Track editing state for each setting
const editingForms = ref({});

// Default message editing (per email type, variants provided by the backend)
const VARIANT_LABELS = {
    site_owner: 'Message to owner',
    external_seller: 'Message to seller',
    client: 'Message to client',
};
const MESSAGE_LOCALES = [
    { label: 'EN', value: 'en' },
    { label: 'PT', value: 'pt' },
    { label: 'ES', value: 'es' },
];
const messageLocale = ref({});

const variantsFor = (setting) => (setting.message_variants || []).map(key => ({
    key,
    label: VARIANT_LABELS[key] || key,
}));

const supportsDefaultMessages = (setting) => variantsFor(setting).length > 0;

const getMessageLocale = (setting) => messageLocale.value[setting.id] || 'en';

const normalizeDefaultMessages = (setting) => {
    const normalized = {};
    variantsFor(setting).forEach(variant => {
        normalized[variant.key] = {};
        MESSAGE_LOCALES.forEach(locale => {
            normalized[variant.key][locale.value] = setting.default_messages?.[variant.key]?.[locale.value] || '';
        });
    });
    return normalized;
};

// Initialize forms for each setting
const initForm = (setting) => {
    if (!editingForms.value[setting.id]) {
        editingForms.value[setting.id] = useForm({
            to_user_ids: setting.to_user_ids || [],
            cc_user_ids: setting.cc_user_ids || [],
            bcc_user_ids: setting.bcc_user_ids || [],
            default_messages: normalizeDefaultMessages(setting),
            is_active: setting.is_active,
        });
    }
    return editingForms.value[setting.id];
};

// Get form for a setting
const getForm = (setting) => {
    return initForm(setting);
};

// Save a setting
const saveSetting = (setting) => {
    const form = getForm(setting);

    form.put(route('admin.email-settings.update', setting.id), {
        onStart: () => showLoading($t('Saving...')),
        onFinish: () => hideLoading(),
        onSuccess: () => {
            toast.add({
                severity: 'success',
                summary: $t('Success'),
                detail: $t('Email setting updated successfully'),
                life: 3000
            });
        },
        onError: (errors) => {
            console.error('Email setting errors:', errors);
            toast.add({
                severity: 'error',
                summary: $t('Error'),
                detail: $t('Failed to update email setting'),
                life: 4000
            });
        }
    });
};

// Toggle active status
const toggleActive = (setting) => {
    router.patch(route('admin.email-settings.toggle', setting.id), {}, {
        onStart: () => showLoading($t('Updating...')),
        onFinish: () => hideLoading(),
        onSuccess: () => {
            // Update local form state
            const form = getForm(setting);
            form.is_active = !form.is_active;

            toast.add({
                severity: 'success',
                summary: $t('Success'),
                detail: $t('Email status updated'),
                life: 3000
            });
        },
        onError: () => {
            toast.add({
                severity: 'error',
                summary: $t('Error'),
                detail: $t('Failed to update status'),
                life: 4000
            });
        }
    });
};

// Get settings for a category
const getSettingsForCategory = (categoryKey) => {
    return props.settings[categoryKey] || [];
};

// Get recipient names for display
const getRecipientNames = (userIds) => {
    if (!userIds || userIds.length === 0) return [];
    return props.adminUsers
        .filter(u => userIds.includes(u.id))
        .map(u => u.name);
};
</script>

<template>
    <!-- Toast is provided globally by AdminMainLayout; a second one renders every message twice. -->

    <div class="p-4">
        <div class="mb-6">
            <h1 class="text-2xl font-bold text-surface-900">{{ $t('Email Configuration') }}</h1>
            <p class="text-surface-600 mt-1">{{ $t('Configure recipients for each system email') }}</p>
        </div>

        <TabView>
            <TabPanel
                v-for="category in categories"
                :key="category.key"
                :header="$t(category.label)"
            >
                <div class="space-y-4">
                    <Card
                        v-for="setting in getSettingsForCategory(category.key)"
                        :key="setting.id"
                        class="shadow-sm"
                    >
                        <template #header>
                            <div class="flex items-center justify-between p-4 pb-0">
                                <div>
                                    <h3 class="text-lg font-semibold text-surface-900">
                                        {{ $t(setting.name) }}
                                    </h3>
                                    <p class="text-sm text-surface-500 mt-1">
                                        {{ $t(setting.description) }}
                                    </p>
                                </div>
                                <div class="flex items-center gap-3">
                                    <Tag
                                        :value="setting.is_client_email ? $t('Client Email') : $t('Admin Email')"
                                        :severity="setting.is_client_email ? 'info' : 'secondary'"
                                    />
                                    <div class="flex items-center gap-2">
                                        <span class="text-sm text-surface-600">{{ $t('Active') }}</span>
                                        <ToggleSwitch
                                            :modelValue="getForm(setting).is_active"
                                            @update:modelValue="toggleActive(setting)"
                                        />
                                    </div>
                                </div>
                            </div>
                        </template>

                        <template #content>
                            <div class="space-y-4">
                                <!-- TO Recipients -->
                                <div class="grid grid-cols-12 gap-4 items-start">
                                    <label class="col-span-2 text-sm font-medium text-surface-700 pt-2">
                                        {{ $t('TO') }}:
                                    </label>
                                    <div class="col-span-10">
                                        <div v-if="setting.is_client_email" class="flex items-center gap-2">
                                            <Tag value="Client" severity="success" />
                                            <span class="text-sm text-surface-500">{{ $t('(automatic - client always receives)') }}</span>
                                        </div>
                                        <MultiSelect
                                            v-else
                                            v-model="getForm(setting).to_user_ids"
                                            :options="adminUsers"
                                            optionLabel="label"
                                            optionValue="id"
                                            :placeholder="$t('Select recipients...')"
                                            class="w-full"
                                            display="chip"
                                            filter
                                        />
                                    </div>
                                </div>

                                <!-- CC Recipients -->
                                <div class="grid grid-cols-12 gap-4 items-start">
                                    <label class="col-span-2 text-sm font-medium text-surface-700 pt-2">
                                        {{ $t('CC') }}:
                                    </label>
                                    <div class="col-span-10">
                                        <MultiSelect
                                            v-model="getForm(setting).cc_user_ids"
                                            :options="adminUsers"
                                            optionLabel="label"
                                            optionValue="id"
                                            :placeholder="$t('Select CC recipients...')"
                                            class="w-full"
                                            display="chip"
                                            filter
                                        />
                                    </div>
                                </div>

                                <!-- BCC Recipients -->
                                <div class="grid grid-cols-12 gap-4 items-start">
                                    <label class="col-span-2 text-sm font-medium text-surface-700 pt-2">
                                        {{ $t('BCC') }}:
                                    </label>
                                    <div class="col-span-10">
                                        <MultiSelect
                                            v-model="getForm(setting).bcc_user_ids"
                                            :options="adminUsers"
                                            optionLabel="label"
                                            optionValue="id"
                                            :placeholder="$t('Select BCC recipients...')"
                                            class="w-full"
                                            display="chip"
                                            filter
                                        />
                                    </div>
                                </div>

                                <!-- Default Messages (publication details email) -->
                                <div v-if="supportsDefaultMessages(setting)">
                                    <Divider />

                                    <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
                                        <div>
                                            <h4 class="text-base font-semibold text-surface-900">
                                                {{ $t('Default email message') }}
                                            </h4>
                                            <p class="text-sm text-surface-500 mt-1">
                                                {{ $t('Leave a field empty to use the built-in translated default.') }}
                                                {{ $t('The email language is chosen when sending each email.') }}
                                            </p>
                                        </div>
                                        <SelectButton
                                            :modelValue="getMessageLocale(setting)"
                                            :options="MESSAGE_LOCALES"
                                            optionLabel="label"
                                            optionValue="value"
                                            :allowEmpty="false"
                                            @update:modelValue="val => messageLocale[setting.id] = val"
                                        />
                                    </div>

                                    <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                        <div
                                            v-for="variant in variantsFor(setting)"
                                            :key="variant.key"
                                            class="p-4 bg-surface-50 rounded-lg border border-surface-200"
                                        >
                                            <label
                                                :for="`default-message-${setting.id}-${variant.key}`"
                                                class="block text-sm font-medium text-surface-700 mb-2"
                                            >
                                                {{ $t(variant.label) }} ({{ getMessageLocale(setting).toUpperCase() }})
                                            </label>
                                            <Textarea
                                                :id="`default-message-${setting.id}-${variant.key}`"
                                                v-model="getForm(setting).default_messages[variant.key][getMessageLocale(setting)]"
                                                rows="7"
                                                autoResize
                                                class="w-full"
                                                maxlength="5000"
                                            />
                                        </div>
                                    </div>

                                    <p class="text-xs text-surface-500 mt-3">
                                        {{ $t('Available placeholders') }}:
                                        <code class="bg-surface-100 px-1 rounded">{{ '{site_url}' }}</code> = {{ $t('site URL' ) }},
                                        <code class="bg-surface-100 px-1 rounded">{{ '{deadline}' }}</code> = {{ $t('publication deadline (3 business days)') }}
                                    </p>
                                </div>

                                <!-- Save Button -->
                                <div class="flex justify-end pt-2">
                                    <Button
                                        :label="$t('Save')"
                                        icon="pi pi-check"
                                        @click="saveSetting(setting)"
                                        :loading="getForm(setting).processing"
                                    />
                                </div>
                            </div>
                        </template>
                    </Card>

                    <div v-if="getSettingsForCategory(category.key).length === 0" class="text-center py-8 text-surface-500">
                        {{ $t('No email settings found for this category') }}
                    </div>
                </div>
            </TabPanel>
        </TabView>
    </div>
</template>

<style scoped>
.space-y-4 > * + * {
    margin-top: 1rem;
}
</style>
